
const libPictProvider = require('pict-provider');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

/**
 * UserGroupGrants — the "Projects Granted" tab of the Access workspace, shown ONLY for a User Group
 * (Role.Type === 'UserGroup'). It answers: "which projects/labs will a user be added to when I put them in
 * this group?" — the effective set, expanded exactly the way the server's member_sync engine materializes it.
 *
 * A User Group's context grants live in the RoleContextMembership table (one row per Role↔Context: Project /
 * Lab / Contract). When a user joins the group, member_sync stamps ProjectUserJoin/LabUserJoin rows with
 * SynthesizedIDRole = the group, from EXACTLY these rows — plus it (a) fans a Contract grant out to that
 * contract's projects and (b) folds in nested child User Groups (this group's RoleEdge children whose Type is
 * UserGroup). This provider reproduces that expansion client-side so the list is truthful.
 *
 * We NEVER use the Home app's `?Joins=` query form (the config app's Meadow endpoint ignores it). Every read is
 * a plain FilteredTo; names are resolved in a second batched FBL~INN call.
 *
 * Editing (add/remove) targets the group's OWN direct RoleContextMembership rows: add = POST RoleContextMembership,
 * remove = DELETE RoleContextMembership/{id}. Grants inherited via a Contract or a nested group are shown but not
 * directly removable here (edit the contract grant, or the nested group).
 *
 * Subject + customer come from the AccessWorkspace provider; this owns AppData.AccessWorkspace.Grants.
 */
class UserGroupGrantsProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get workspace() { return this.pict.providers.AccessWorkspace; }
	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get grants()
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.Grants)
		{
			tmpWorkspaceState.Grants =
			{
				SubjectID: 0, Loading: false, Loaded: false, Error: '', Truncated: false,
				Projects: [], Labs: [], Contracts: [],
				// Add-a-grant control state (the picker value addresses).
				AddScope: 'Project', AddContextID: '', AddSaving: false, AddError: '', AddNotice: '', RemovingID: 0,
			};
		}
		return tmpWorkspaceState.Grants;
	}

	// --- REST (mirrors GroupComposition) ---

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pError, pBody2) => pError ? reject(pError) : resolve(pBody2))); }
	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			const tmpMatch = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (tmpMatch) { this.restClient.deleteEntity(tmpMatch[1], tmpMatch[2], (pError, pBody) => pError ? reject(pError) : resolve(pBody)); return; }
			const tmpBase = this.restClient.serverURL || '/1.0/';
			fetch(`${tmpBase}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((pResponse) => { if (!pResponse.ok) { reject(new Error(`HTTP ${pResponse.status}`)); return; } pResponse.text().then((pText) => resolve(pText ? JSON.parse(pText) : {})).catch(() => resolve({})); })
				.catch(reject);
		});
	}

	/** Count-then-page a filtered set (no ?Joins=). */
	async _getFilteredAll(pEntitySet, pFilter, pCap)
	{
		const tmpCap = pCap || 2000;
		let tmpTotal = tmpCap;
		try { const tmpCount = await this._getJSON(`${pEntitySet}/Count/FilteredTo/${pFilter}`); tmpTotal = Math.min((tmpCount && tmpCount.Count) || 0, tmpCap); }
		catch (pError) { tmpTotal = tmpCap; }
		if (!tmpTotal) { return { rows: [], truncated: false }; }
		const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/${pFilter}/0/${tmpTotal}`);
		return { rows: (Array.isArray(tmpRows) ? tmpRows : []), truncated: (tmpTotal >= tmpCap) };
	}

	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	/** Batch-resolve entity ids → names (no ?Joins=): FBL~<IDField>~INN~<ids>. Best-effort; unknown ids fall back to #id. */
	async _resolveNames(pEntitySet, pIDField, pIDs)
	{
		const tmpUnique = Array.from(new Set((pIDs || []).map(Number).filter((pID) => pID > 0)));
		const tmpMap = {};
		if (!tmpUnique.length) { return tmpMap; }
		// Chunk the INN list so a group granting hundreds of contexts doesn't build an unbounded filter.
		const tmpChunk = 200;
		for (let i = 0; i < tmpUnique.length; i += tmpChunk)
		{
			const tmpSlice = tmpUnique.slice(i, i + tmpChunk);
			try
			{
				/* eslint-disable-next-line no-await-in-loop */
				const tmpResult = await this._getFilteredAll(pEntitySet, `FBL~${pIDField}~INN~${tmpSlice.join(',')}~FBV~Deleted~EQ~0`, tmpChunk);
				tmpResult.rows.forEach((pRow) => { if (pRow && (pRow[pIDField] != null)) { tmpMap[Number(pRow[pIDField])] = pRow.Name || pRow.Description || ''; } });
			}
			catch (pError) { /* leave unresolved ids to fall back to #id */ }
		}
		return tmpMap;
	}

	// --- Load + expand (the truthful effective set) ---

	/**
	 * Collect the RoleContextMemberships that member_sync would materialize for a user placed in this group:
	 * the group's OWN rows (marked direct, carrying the IDRoleContextMembership so they can be removed) plus
	 * those of its DIRECT child User Groups. This is ONE level only — it mirrors member_sync's
	 * `resolveRoleContextMemberships` exactly ("for now, looking 1 level in both directions"): it does NOT
	 * recurse into grandchildren, so it never shows a project a user would not actually receive. Child-group
	 * Types/Names are resolved from the SERVER (`Roles/…INN…~FBV~Type~EQ~UserGroup`), not the partial rail list.
	 */
	async _collectGrants(pTopIDRole)
	{
		const tmpTop = Number(pTopIDRole);
		const tmpGrants = [];
		let tmpTruncated = false;
		// Direct child User Groups (one level): RoleEdges with ParentIDRole=top, children whose Type='UserGroup'.
		const tmpEdges = await this._getFilteredAll('RoleEdges', `FBV~ParentIDRole~EQ~${tmpTop}~FBV~Deleted~EQ~0`);
		const tmpChildIDs = Array.from(new Set(tmpEdges.rows.map((pEdge) => Number(pEdge.ChildIDRole)).filter((pID) => pID > 0 && pID !== tmpTop)));
		const tmpChildGroupNames = {};
		for (let i = 0; i < tmpChildIDs.length; i += 150)
		{
			const tmpSlice = tmpChildIDs.slice(i, i + 150);
			/* eslint-disable-next-line no-await-in-loop */
			const tmpRoles = await this._getFilteredAll('Roles', `FBL~IDRole~INN~${tmpSlice.join(',')}~FBV~Type~EQ~UserGroup~FBV~Deleted~EQ~0`, 500);
			tmpRoles.rows.forEach((pRole) => { tmpChildGroupNames[Number(pRole.IDRole)] = pRole.Name || `#${pRole.IDRole}`; });
		}
		// Fetch RCMs for the group + its direct child User Groups (batched INN — one query per ~150 ids).
		const tmpRoleIDs = [ tmpTop ].concat(Object.keys(tmpChildGroupNames).map(Number));
		for (let i = 0; i < tmpRoleIDs.length; i += 150)
		{
			const tmpSlice = tmpRoleIDs.slice(i, i + 150);
			/* eslint-disable-next-line no-await-in-loop */
			const tmpRCM = await this._getFilteredAll('RoleContextMemberships', `FBL~IDRole~INN~${tmpSlice.join(',')}`);
			if (tmpRCM.truncated) { tmpTruncated = true; }
			tmpRCM.rows.forEach((pRow) =>
			{
				const tmpIDRole = Number(pRow.IDRole);
				const tmpIsTop = (tmpIDRole === tmpTop);
				tmpGrants.push(
					{
						Context: pRow.Context, IDContext: Number(pRow.IDContext) || 0,
						IDRCM: tmpIsTop ? (Number(pRow.IDRoleContextMembership) || 0) : 0,
						SourceLabel: tmpIsTop ? 'direct' : `via group ${tmpChildGroupNames[tmpIDRole] || `#${tmpIDRole}`}`,
					});
			});
		}
		return { grants: tmpGrants, truncated: tmpTruncated };
	}

	async loadGrantsAsync(pForce)
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.SubjectRecord || !this.workspace.isUserGroup) { return; }
		const tmpID = Number(tmpWorkspaceState.SubjectID);
		const tmpGrants = this.grants;
		if (tmpGrants.Loaded && !pForce && (tmpGrants.SubjectID === tmpID)) { this._render(); return; }
		tmpGrants.SubjectID = tmpID; tmpGrants.Loaded = false; tmpGrants.Error = ''; tmpGrants.Loading = true; tmpGrants.Truncated = false;
		this._render();
		try
		{
			const tmpCollected = await this._collectGrants(tmpID);
			const tmpAll = tmpCollected.grants;
			const tmpProjectGrants = tmpAll.filter((pGrant) => pGrant.Context === 'Project');
			const tmpLabGrants = tmpAll.filter((pGrant) => pGrant.Context === 'Lab');
			const tmpContractGrants = tmpAll.filter((pGrant) => pGrant.Context === 'Contract');

			// Resolve names for the directly-referenced projects / labs / contracts.
			const tmpProjNames = await this._resolveNames('Projects', 'IDProject', tmpProjectGrants.map((pGrant) => pGrant.IDContext));
			const tmpLabNames = await this._resolveNames('Labs', 'IDLab', tmpLabGrants.map((pGrant) => pGrant.IDContext));
			const tmpContractNames = await this._resolveNames('Contracts', 'IDContract', tmpContractGrants.map((pGrant) => pGrant.IDContext));

			// Build the effective PROJECT set (deduped), tracking each source + whether a direct grant is removable.
			const tmpProjectsByID = {};
			const fAddProject = (pIDProject, pName, pSourceLabel, pDirectIDRCM) =>
			{
				const tmpKey = Number(pIDProject) || 0;
				if (!tmpKey) { return; }
				if (!tmpProjectsByID[tmpKey]) { tmpProjectsByID[tmpKey] = { ID: tmpKey, Name: '', Sources: {}, DirectIDRCM: 0 }; }
				const tmpEntry = tmpProjectsByID[tmpKey];
				if (pName && (!tmpEntry.Name || tmpEntry.Name.charAt(0) === '#')) { tmpEntry.Name = pName; }
				tmpEntry.Sources[pSourceLabel] = true;
				if (pDirectIDRCM) { tmpEntry.DirectIDRCM = pDirectIDRCM; }
			};
			// Direct Project grants — drop any whose project no longer resolves (deleted), matching member_sync,
			// which filters RCMs pointing at deleted context entities before it materializes anything.
			tmpProjectGrants.forEach((pGrant) => { if (tmpProjNames[pGrant.IDContext]) { fAddProject(pGrant.IDContext, tmpProjNames[pGrant.IDContext], pGrant.SourceLabel, pGrant.IDRCM); } });
			// Fan each Contract grant out to that contract's projects (what member_sync's resolveContractProjectIDs
			// does). Skip a grant whose contract itself is deleted (unresolved name) — member_sync drops it too.
			for (let i = 0; i < tmpContractGrants.length; i++)
			{
				const tmpContract = tmpContractGrants[i];
				if (!tmpContractNames[tmpContract.IDContext]) { continue; }
				const tmpLabel = `via contract ${tmpContractNames[tmpContract.IDContext]}`;
				/* eslint-disable-next-line no-await-in-loop */
				const tmpContractProjects = await this._getFilteredAll('Projects', `FBV~IDContract~EQ~${tmpContract.IDContext}~FBV~Deleted~EQ~0`);
				if (tmpContractProjects.truncated) { tmpCollected.truncated = true; }
				tmpContractProjects.rows.forEach((pProject) => fAddProject(Number(pProject.IDProject), pProject.Name, tmpLabel, 0));
			}

			// Effective LAB set (no fan-out); drop deleted labs the same way.
			const tmpLabsByID = {};
			tmpLabGrants.forEach((pGrant) =>
			{
				const tmpKey = Number(pGrant.IDContext) || 0;
				if (!tmpKey || !tmpLabNames[tmpKey]) { return; }
				if (!tmpLabsByID[tmpKey]) { tmpLabsByID[tmpKey] = { ID: tmpKey, Name: tmpLabNames[tmpKey], Sources: {}, DirectIDRCM: 0 }; }
				tmpLabsByID[tmpKey].Sources[pGrant.SourceLabel] = true;
				if (pGrant.IDRCM) { tmpLabsByID[tmpKey].DirectIDRCM = pGrant.IDRCM; }
			});

			// The group's OWN direct Contract grants (removable; shown as a section). Deleted contracts dropped.
			const tmpDirectContracts = {};
			tmpContractGrants.filter((pGrant) => pGrant.IDRCM && tmpContractNames[pGrant.IDContext]).forEach((pGrant) =>
			{
				const tmpKey = Number(pGrant.IDContext) || 0;
				if (tmpKey && !tmpDirectContracts[tmpKey]) { tmpDirectContracts[tmpKey] = { ID: tmpKey, Name: tmpContractNames[tmpKey], IDRCM: pGrant.IDRCM }; }
			});

			const fSort = (pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || ''));
			tmpGrants.Projects = Object.keys(tmpProjectsByID).map((pKey) => this._effectiveRow(tmpProjectsByID[pKey])).sort(fSort);
			tmpGrants.Labs = Object.keys(tmpLabsByID).map((pKey) => this._effectiveRow(tmpLabsByID[pKey])).sort(fSort);
			tmpGrants.Contracts = Object.keys(tmpDirectContracts).map((pKey) => tmpDirectContracts[pKey]).sort(fSort);
			tmpGrants.Truncated = tmpCollected.truncated;
			tmpGrants.Loaded = true;
		}
		catch (pError) { tmpGrants.Error = `Could not load granted projects: ${pError && pError.message ? pError.message : pError}`; }
		tmpGrants.Loading = false;
		this._render();
	}

	_effectiveRow(pEntry)
	{
		const tmpSources = Object.keys(pEntry.Sources || {});
		return { ID: pEntry.ID, Name: pEntry.Name || `#${pEntry.ID}`, Sources: tmpSources, DirectIDRCM: pEntry.DirectIDRCM || 0 };
	}

	// --- Mutations (edit the group's OWN direct grants) ---

	setAddScope(pScope) { this.grants.AddScope = (pScope === 'Lab' || pScope === 'Contract') ? pScope : 'Project'; this.grants.AddContextID = ''; this.grants.AddError = ''; this.grants.AddNotice = ''; this._render(); }

	async addGrantAsync()
	{
		const tmpGrants = this.grants;
		if (!this.workspace.isRoleManager || tmpGrants.AddSaving) { return; }
		const tmpIDRole = Number(this.workspace.state.SubjectID);
		const tmpScope = tmpGrants.AddScope || 'Project';
		const tmpIDContext = Number(tmpGrants.AddContextID) || 0;
		if (!(tmpIDRole > 0)) { return; }
		if (!tmpIDContext) { tmpGrants.AddError = `Pick a ${tmpScope.toLowerCase()} to grant.`; tmpGrants.AddNotice = ''; this._render(); return; }
		tmpGrants.AddSaving = true; tmpGrants.AddError = ''; tmpGrants.AddNotice = '';
		this._render();
		const tmpRes = await this._postJSON('RoleContextMembership', { Context: tmpScope, IDContext: tmpIDContext, IDRole: tmpIDRole, IDCustomer: Number(this.workspace.idCustomer) || 0 });
		tmpGrants.AddSaving = false;
		if (tmpRes && tmpRes.error) { tmpGrants.AddError = `Could not add the grant: ${(tmpRes.error && tmpRes.error.message) || tmpRes.error} (you may lack permission).`; this._render(); return; }
		tmpGrants.AddNotice = `${tmpScope} grant added.`;
		tmpGrants.AddContextID = '';
		await this.loadGrantsAsync(true);
	}

	async removeGrantAsync(pIDRCM)
	{
		const tmpGrants = this.grants;
		if (!this.workspace.isRoleManager || tmpGrants.RemovingID) { return; }
		const tmpID = Number(pIDRCM) || 0;
		if (!tmpID) { return; }
		tmpGrants.RemovingID = tmpID; tmpGrants.AddError = ''; tmpGrants.AddNotice = '';
		this._render();
		try { await this._deleteJSON(`RoleContextMembership/${tmpID}`); tmpGrants.AddNotice = 'Grant removed.'; }
		catch (pError) { tmpGrants.RemovingID = 0; tmpGrants.AddError = `Could not remove the grant: ${pError && pError.message ? pError.message : pError}`; this._render(); return; }
		tmpGrants.RemovingID = 0;
		await this.loadGrantsAsync(true);
	}

	// --- Add-a-grant context picker (Project / Lab / Contract) ---

	_contextDataProvider(pScope)
	{
		const tmpMap = { Lab: { Set: 'Labs', ID: 'IDLab' }, Contract: { Set: 'Contracts', ID: 'IDContract' } };
		const tmpDef = tmpMap[pScope] || { Set: 'Projects', ID: 'IDProject' };
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSON(pURL),
			{
				Set: tmpDef.Set, IDField: tmpDef.ID, PageSize: 40, Scope: `FBV~IDCustomer~EQ~${Number(this.workspace.idCustomer) || 0}~FBV~Deleted~EQ~0`,
				ToOption: (pRow) => ({ Value: Number(pRow[tmpDef.ID]), Text: pRow.Name || pRow.Description || `#${pRow[tmpDef.ID]}`, Record: pRow }),
			});
	}
	_contextResolveValue(pScope)
	{
		const tmpMap = { Lab: { Set: 'Lab', ID: 'IDLab' }, Contract: { Set: 'Contract', ID: 'IDContract' } };
		const tmpDef = tmpMap[pScope] || { Set: 'Project', ID: 'IDProject' };
		return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`${tmpDef.Set}/${tmpID}`).then((pRow) => ((pRow && pRow[tmpDef.ID]) ? { Value: tmpID, Text: pRow.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` })).catch(() => ({ Value: tmpID, Text: `#${tmpID}` })); };
	}

	mountGrantPicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#acw-grant-picker').length === 0) { return; }
		const tmpScope = this.grants.AddScope || 'Project';
		const tmpView = tmpPicker.createPicker('AccessWorkspace-GrantPicker',
			{
				DestinationAddress: '#acw-grant-picker', ValueAddress: 'AppData.AccessWorkspace.Grants.AddContextID',
				Searchable: true, AllowClear: true, ClearLabel: `— pick a ${tmpScope.toLowerCase()} —`, Placeholder: `Search ${tmpScope.toLowerCase()}s…`,
				DataProvider: this._contextDataProvider(tmpScope), ResolveValue: this._contextResolveValue(tmpScope),
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
		if (tmpView && (typeof tmpView.setValue === 'function')) { tmpView.setValue(this.grants.AddContextID || ''); }
	}

	// --- Render (host-div paint, called from AccessWorkspace.onAfterRender) ---

	renderGrantsHost()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#acw-grants-host').length === 0) { return; }
		tmpCA.assignContent('#acw-grants-host', this._grantsHostHTML());
		this.mountGrantPicker();
	}

	_sourceBadgesHTML(pRow)
	{
		const tmpSources = pRow.Sources || [];
		// A popular project can be granted by a dozen sibling groups; showing every badge makes an unreadably tall
		// row, so cap the visible badges and fold the rest into a "+N more" chip that lists them all on hover.
		const tmpCap = 4;
		const tmpShown = tmpSources.slice(0, tmpCap).map((pSource) =>
			(pSource === 'direct')
				? '<span class="acw-grant-src acw-grant-src-direct">direct</span>'
				: `<span class="acw-grant-src acw-grant-src-via">${this._esc(pSource)}</span>`).join('');
		if (tmpSources.length <= tmpCap) { return tmpShown; }
		const tmpRest = tmpSources.slice(tmpCap);
		return `${tmpShown}<span class="acw-grant-src acw-grant-src-more" title="${this._esc(tmpRest.join('  •  '))}">+${tmpRest.length} more</span>`;
	}

	_effectiveRowHTML(pRow, pKind)
	{
		const tmpRemovable = this.workspace.isRoleManager && pRow.DirectIDRCM;
		const tmpRemove = tmpRemovable
			? `<button class="acw-grant-x" title="Remove this ${pKind} grant from the group" onclick="_Pict.providers.UserGroupGrants.removeGrantAsync(${Number(pRow.DirectIDRCM)})"><span class="icon is-small"><i class="fas fa-xmark"></i></span></button>` : '';
		return `<div class="acw-grant-row"><span class="acw-grant-name">${this._esc(pRow.Name)}</span><span class="acw-grant-id">#${Number(pRow.ID)}</span><span class="acw-grant-srcs">${this._sourceBadgesHTML(pRow)}</span>${tmpRemove}</div>`;
	}

	_sectionHTML(pTitle, pRows, pKind, pEmptyText)
	{
		// Cap the rendered rows — a group granting a large contract can fan out to hundreds of projects; render the
		// first RENDER_CAP and note the rest so the DOM stays light and the count stays honest.
		const tmpRenderCap = 300;
		let tmpBody;
		if (!pRows.length) { tmpBody = `<p class="acw-grant-empty">${this._esc(pEmptyText)}</p>`; }
		else
		{
			tmpBody = pRows.slice(0, tmpRenderCap).map((pRow) => this._effectiveRowHTML(pRow, pKind)).join('');
			if (pRows.length > tmpRenderCap) { tmpBody += `<p class="acw-grant-empty">Showing the first ${tmpRenderCap} of ${pRows.length}.</p>`; }
		}
		return `<div class="acw-grant-section"><div class="acw-grant-head"><span>${this._esc(pTitle)}</span><span class="acw-grant-count">${pRows.length}</span></div>${tmpBody}</div>`;
	}

	_contractSectionHTML(pContracts)
	{
		if (!pContracts.length) { return ''; }
		const tmpRows = pContracts.map((pContract) =>
		{
			const tmpRemove = this.workspace.isRoleManager
				? `<button class="acw-grant-x" title="Remove this contract grant" onclick="_Pict.providers.UserGroupGrants.removeGrantAsync(${Number(pContract.IDRCM)})"><span class="icon is-small"><i class="fas fa-xmark"></i></span></button>` : '';
			return `<div class="acw-grant-row"><span class="icon acw-grant-cicon"><i class="fas fa-file-contract"></i></span><span class="acw-grant-name">${this._esc(pContract.Name)}</span><span class="acw-grant-id">#${Number(pContract.ID)}</span><span class="acw-grant-srcs"><span class="acw-grant-src acw-grant-src-via">covers its projects (above)</span></span>${tmpRemove}</div>`;
		}).join('');
		return `<div class="acw-grant-section"><div class="acw-grant-head"><span>Contract grants</span><span class="acw-grant-count">${pContracts.length}</span></div>${tmpRows}</div>`;
	}

	_addBlockHTML()
	{
		if (!this.workspace.isRoleManager) { return ''; }
		const tmpGrants = this.grants;
		const tmpScope = tmpGrants.AddScope || 'Project';
		const tmpNotice = tmpGrants.AddNotice ? `<div class="acw-grant-notice">${this._esc(tmpGrants.AddNotice)}</div>` : '';
		const tmpError = tmpGrants.AddError ? `<div class="acw-grant-error">${this._esc(tmpGrants.AddError)}</div>` : '';
		const tmpBusy = tmpGrants.AddSaving ? '<span class="acw-grant-busy">saving…</span>' : '';
		return `<div class="acw-grant-add">
			<div class="acw-grant-add-head">Grant a project, lab, or contract to this group</div>
			<div class="acw-grant-add-row">
				<div class="acw-grant-field"><label class="acw-grant-flabel">Type</label>
					<div class="select"><select onchange="_Pict.providers.UserGroupGrants.setAddScope(this.value)">
						<option value="Project"${tmpScope === 'Project' ? ' selected' : ''}>Project</option>
						<option value="Lab"${tmpScope === 'Lab' ? ' selected' : ''}>Lab</option>
						<option value="Contract"${tmpScope === 'Contract' ? ' selected' : ''}>Contract</option>
					</select></div>
				</div>
				<div class="acw-grant-field"><label class="acw-grant-flabel">${this._esc(tmpScope)}</label><div id="acw-grant-picker" class="acw-grant-picker"></div></div>
				<button class="acw-grant-btn" onclick="_Pict.providers.UserGroupGrants.addGrantAsync()">Add grant</button>${tmpBusy}
			</div>
			${tmpNotice}${tmpError}
		</div>`;
	}

	_grantsHostHTML()
	{
		const tmpGrants = this.grants;
		const tmpHelp = `<p class="pw-help" style="margin:0 0 0.8rem">The projects and labs a user is added to when placed in this group — including projects from any <strong>contract</strong> grants and any <strong>nested user groups</strong> this group includes. Direct grants can be removed here.</p>`;
		if (tmpGrants.Loading && !tmpGrants.Loaded) { return `${tmpHelp}<div class="pw-empty box has-text-grey has-text-centered"><span class="pw-spinner"></span><p class="mt-3">Loading granted projects…</p></div>`; }
		if (tmpGrants.Error && !tmpGrants.Loaded) { return `${tmpHelp}<div class="notification is-warning is-soft py-2 px-3">${this._esc(tmpGrants.Error)}</div>${this._addBlockHTML()}`; }
		const tmpTrunc = tmpGrants.Truncated ? `<div class="acw-grant-notice">Some lists were large and may be truncated.</div>` : '';
		return `${tmpHelp}${tmpTrunc}`
			+ this._sectionHTML('Projects', tmpGrants.Projects, 'project', 'This group grants no projects.')
			+ this._sectionHTML('Labs', tmpGrants.Labs, 'lab', 'This group grants no labs.')
			+ this._contractSectionHTML(tmpGrants.Contracts)
			+ this._addBlockHTML();
	}

	_render() { if (this.pict.views.AccessWorkspace) { this.pict.views.AccessWorkspace.render(); } }
}

module.exports = UserGroupGrantsProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'UserGroupGrants',
	AutoInitialize: true,
	AutoInitializeOrdinal: 4,
};
